const chapter = {
  id: 'biotech-principles-processes',
  slug: 'biotech-principles-processes',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Biotechnology — Principles and Processes',
        hindi: 'जैव प्रौद्योगिकी — सिद्धान्त और प्रक्रियाएँ',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '3 questions in NEET',
        hindi: 'NEET में 3 प्रश्न दो व्यापक प्रकार: **मेरिस्टेमेटिक** (विभाजन) + **स्थायी** (कोई विभाजन नहीं)। **मेरिस्टेम्स**:',
  },
  syllabusTags: ['Biotechnology', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'principles',
      title: {
        english: 'Principles of Biotechnology',
        hindi: 'जैव प्रौद्योगिकी के सिद्धान्त',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'EFB (1989) definition: integration of natural sciences + engineering to apply organisms/cells/parts/molecular analogues for products & services.',
        hindi: 'EFB (1989) परिभाषा: उत्पादों और सेवाओं के लिए organisms/cells/parts/molecular एनालॉग लागू करने के लिए प्राकृतिक विज्ञान + इंजीनियरिंग का एकीकरण।',
            },
            {
              english: 'Modern biotech = **Genetic Engineering (rDNA technology)** + maintenance of sterile (contamination-free) microbial / animal / plant cell culture for **bioprocess engineering**.',
        hindi: 'आधुनिक बायोटेक = **जेनेटिक इंजीनियरिंग (आरडीएनए प्रौद्योगिकी)** + **बायोप्रोसेस इंजीनियरिंग** के लिए बाँझ (संदूषण मुक्त) माइक्रोबियल / पशु / पौधे कोशिका संस्कृति का रखरखाव।',
            },
            {
              english: 'Three core techniques:',
        hindi: 'तीन मुख्य तकनीकें:',
            },
            {
              english: '• **Genetic engineering** — alter chemistry of genetic material (DNA / RNA), introduce into host, change phenotype.',
        hindi: '• **जेनेटिक इंजीनियरिंग** - आनुवंशिक सामग्री (डीएनए/आरएनए) के रसायन विज्ञान को बदलें, मेजबान में डालें, फेनोटाइप बदलें।',
            },
            {
              english: '• **Bioprocess engineering** — large-scale culture for industrial production.',
        hindi: '• **बायोप्रोसेस इंजीनियरिंग** - औद्योगिक उत्पादन के लिए बड़े पैमाने पर संस्कृति।',
            },
            {
              english: '• Maintenance of sterile (aseptic) ambience.',
        hindi: '• बाँझ (सड़न रोकनेवाला) माहौल का रखरखाव। **स्टेनली कोहेन और हर्बर्ट बॉयर (1972)** - पहला **rDNA अणु** (*साल्मोनेला* से एंटीबायोटिक प्रतिरोध जीन + *ई. कोली* का प्लास्मिड)।',
            },
            {
              english: '**Stanley Cohen & Herbert Boyer (1972)** — first **rDNA molecule** (antibiotic resistance gene from *Salmonella* + plasmid of *E. coli*).',
        hindi: '**स्टेनली कोहेन और हर्बर्ट बॉयर (1972)** - पहला **आरडीएनए अणु** (*साल्मोनेला* से एंटीबायोटिक प्रतिरोध जीन + *ई. कोली* का प्लास्मिड)।',
            },
          ],
        },
      ],
    },
    {
      id: 'tools',
      title: {
        english: 'Tools of Recombinant DNA Technology',
        hindi: 'पुनर्योगज DNA प्रौद्योगिकी के उपकरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**1. Restriction enzymes (molecular scissors)** —',
        hindi: '**1. प्रतिबंध एंजाइम (आणविक कैंची)** -',
            },
            {
              english: '• **Endonucleases** cut DNA at specific recognition sites; **exonucleases** remove from ends.',
        hindi: '• **एंडोन्यूक्लाइजेस** विशिष्ट पहचान स्थलों पर डीएनए को काटते हैं; **एक्सोन्यूक्लिअस** सिरों से हटा दें।',
            },
            {
              english: '• First isolated: **Hind II** (1968).',
        hindi: '• पहला पृथक: **हिंद II** (1968)। • **EcoRI** (*Escherichia coli* से) - 6-बीपी **palindromic** अनुक्रम (GAATTC) को पहचानता है → **चिपचिपे सिरे** देने के लिए कट करता है।',
            },
            {
              english: '• **EcoRI** (from *Escherichia coli*) — recognises 6-bp **palindromic** sequence (GAATTC) → cuts to give **sticky ends**.',
        hindi: '• **इकोआरआई** (*एस्चेरिचिया कोली* से) - 6-बीपी **पेलिंड्रोमिक** अनुक्रम (GAATTC) को पहचानता है → **चिपचिपे सिरे** देने के लिए कट करता है।',
            },
            {
              english: '• Naming convention: 1st letter = genus (italics), next 2 = species, then strain + Roman numeral order of discovery (e.g. Eco RI).',
        hindi: '• नामकरण परंपरा: पहला letter = genus (italics), अगली 2 = प्रजाति, फिर स्ट्रेन + खोज का रोमन अंक क्रम (जैसे इको आरआई)।',
            },
            {
              english: '• Sticky ends = single-stranded overhangs → H-bond with complementary cut → joined by **DNA ligase**.',
        hindi: '• चिपचिपे सिरे = एकल-फंसे हुए ओवरहैंग → पूरक कट के साथ एच-बॉन्ड → **डीएनए लिगेज** से जुड़े हुए।',
            },
            {
              english: '**2. Cloning vectors** —',
        hindi: '**2. क्लोनिंग वैक्टर** —',
            },
            {
              english: '• Carriers that replicate within host. Features needed: **ori** (origin of replication), **selectable marker** (antibiotic resistance — ampicillin/tetracycline), **cloning sites** (single restriction sites, often within marker → insertional inactivation), and small size.',
        hindi: '• वाहक जो होस्ट के भीतर प्रतिकृति बनाते हैं। आवश्यक सुविधाएँ: **ओआरआई** (प्रतिकृति की उत्पत्ति), **चयन योग्य मार्कर** (एंटीबायोटिक प्रतिरोध - ampicillin/tetracycline), **क्लोनिंग साइटें** (एकल प्रतिबंध साइटें, अक्सर मार्कर → सम्मिलन निष्क्रियता के भीतर), और छोटा आकार।',
            },
            {
              english: '• Examples: **pBR322** plasmid (rop, ori, ampR, tetR, multiple cloning sites), **bacteriophage λ**, **YAC**, **BAC**.',
        hindi: '• उदाहरण: **pBR322** प्लास्मिड (आरपी, ओरी, एम्पआर, टीईटीआर, मल्टीपल क्लोनिंग साइट्स), **बैक्टीरियोफेज λ**, **YAC**, **BAC**।',
            },
            {
              english: '• Plant: **Ti plasmid of Agrobacterium tumefaciens** (natural genetic engineer; T-DNA integrates into plant genome).',
        hindi: '• पौधा: **एग्रोबैक्टीरियम ट्यूमफेशियन्स का Ti प्लास्मिड** (प्राकृतिक आनुवंशिक इंजीनियर; T-DNA पौधे के जीनोम में एकीकृत होता है)।',
            },
            {
              english: '• Animal: **retroviruses** (transform normal cells to cancerous; modified for gene delivery).',
        hindi: '• पशु: **रेट्रोवायरस** (सामान्य कोशिकाओं को कैंसर में बदल देते हैं; जीन वितरण के लिए संशोधित)।',
            },
            {
              english: '**3. Competent host** — must take up DNA. Made competent by:',
        hindi: '**3. सक्षम मेज़बान** - को डीएनए अवश्य लेना चाहिए। इनके द्वारा सक्षम बनाया गया:',
            },
            {
              english: '• Treatment with divalent **Ca²⁺** + brief **heat shock at 42 °C** → 4 °C.',
        hindi: '• डाइवेलेंट **Ca²⁺** + संक्षिप्त **हीट शॉक 42 डिग्री सेल्सियस** → 4 डिग्री सेल्सियस पर उपचार।',
            },
            {
              english: '• **Microinjection** (animal cells), **biolistics / gene gun** (plants — gold/tungsten micro-projectiles), **disarmed pathogen vectors** (Agrobacterium).',
        hindi: '• **माइक्रोइंजेक्शन** (पशु कोशिकाएं), **बायोलिस्टिक्स / जीन गन** (पौधे - gold/tungsten माइक्रो-प्रोजेक्टाइल), **निःशस्त्र रोगज़नक़ वैक्टर** (एग्रोबैक्टीरियम)।',
            },
            {
              english: '**4. DNA polymerase** (thermostable **Taq** from *Thermus aquaticus*) — for PCR amplification.',
        hindi: '**4. डीएनए पोलीमरेज़** (थर्मस एक्वाटिकस से थर्मोस्टेबल टाक) - पीसीआर प्रवर्धन के लिए।',
            },
          ],
        },
      ],
    },
    {
      id: 'rdna-steps',
      title: {
        english: 'Recombinant DNA Technology — Steps',
        hindi: 'पुनर्योगज DNA प्रौद्योगिकी — चरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**1. Isolation of DNA** — break cells (lysozyme for bacteria, cellulase plants, chitinase fungi); RNase + protease remove RNA & protein; chilled **ethanol** → DNA precipitates as fine threads.',
        hindi: '**1. डीएनए का अलगाव** - कोशिकाओं को तोड़ना (बैक्टीरिया के लिए लाइसोजाइम, सेल्यूलस पौधे, चिटिनेज कवक); RNase + प्रोटीज़ RNA और प्रोटीन को हटाता है; ठंडा **एथेनॉल** → डीएनए महीन धागों के रूप में अवक्षेपित होता है।',
            },
            {
              english: '**2. Fragmentation by restriction enzyme** — agarose gel electrophoresis (DNA migrates from −ve to +ve; smaller migrates faster); visualised with **ethidium bromide (EtBr)** under UV → orange bands; **elution** = cutting bands & purifying.',
        hindi: '**2. प्रतिबंध एंजाइम द्वारा विखंडन** - एगरोज़ जेल वैद्युतकणसंचलन (डीएनए −ve से +ve में स्थानांतरित होता है; छोटे लोग तेजी से पलायन करते हैं); यूवी → नारंगी बैंड के तहत **एथिडियम ब्रोमाइड (ईटीबीआर)** के साथ दृश्यमान; **एल्यूशन** = बैंड काटना और शुद्ध करना।',
            },
            {
              english: '**3. Ligation with vector** — both DNA + vector cut by **same restriction enzyme** → complementary sticky ends → joined by **DNA ligase**.',
        hindi: '**3. सदिश के साथ बंधाव** - डीएनए + सदिश दोनों **एक ही प्रतिबंध एंजाइम** द्वारा कटते हैं → पूरक चिपचिपे सिरे → **डीएनए लिगेज** से जुड़े होते हैं।',
            },
            {
              english: '**4. Insertion into host** — competent cell + rDNA → host transformation.',
        hindi: '**4. होस्ट में सम्मिलन** - सक्षम सेल + आरडीएनए → होस्ट परिवर्तन।',
            },
            {
              english: '**5. Selection of recombinants** — by antibiotic resistance / **insertional inactivation of marker gene**; modern vectors use chromogenic substrate giving **blue-white colour** with β-galactosidase (lacZ insertional inactivation).',
        hindi: '**5. पुनः संयोजकों का चयन** - एंटीबायोटिक प्रतिरोध द्वारा / **मार्कर जीन का सम्मिलन निष्क्रियता**; आधुनिक सदिश क्रोमोजेनिक सब्सट्रेट का उपयोग करते हैं जो β-गैलेक्टोसिडेज़ (lacZ इंसर्शनल इनएक्टिवेशन) के साथ **नीला-सफ़ेद रंग** देता है।',
            },
            {
              english: '**6. Bioreactor culture** — large-scale (100-1000 L) — **stirred-tank reactor** most common; **sparged-stirred-tank** for O₂. Provides optimal pH, T, substrate, salts, vitamins, O₂.',
        hindi: '**6. बायोरिएक्टर कल्चर** - बड़े पैमाने पर (100-1000 एल) - **स्टिरर्ड-टैंक रिएक्टर** सबसे आम; O₂ के लिए **स्पर्ज्ड-स्टिरर्ड-टैंक**। इष्टतम pH, T, सब्सट्रेट, लवण, विटामिन, O₂ प्रदान करता है।',
            },
            {
              english: '**7. Downstream processing** — separation + purification + formulation + clinical trials + quality control.',
        hindi: '**7. डाउनस्ट्रीम प्रसंस्करण** - पृथक्करण + शुद्धिकरण + सूत्रीकरण + नैदानिक ​​​​परीक्षण + गुणवत्ता नियंत्रण।',
            },
          ],
        },
      ],
    },
    {
      id: 'pcr',
      title: {
        english: 'PCR (Polymerase Chain Reaction)',
        hindi: 'PCR (पॉलीमेरेज़ श्रृंखला अभिक्रिया)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Amplifies a specific DNA segment in vitro. Invented by **Kary Mullis (1983, Nobel 1993)**.',
        hindi: 'इन विट्रो में एक विशिष्ट डीएनए खंड को बढ़ाता है। **कैरी मुलिस (1983, नोबेल 1993)** द्वारा आविष्कार किया गया।',
            },
            {
              english: 'Three steps per cycle (in thermal cycler):',
        hindi: 'प्रति चक्र तीन चरण (थर्मल साइक्लर में):',
            },
            {
              english: '• **Denaturation** — 94 °C, double-strand → 2 single strands.',
        hindi: '• **विकृतीकरण** - 94 डिग्री सेल्सियस, डबल-स्ट्रैंड → 2 सिंगल स्ट्रैंड।',
            },
            {
              english: '• **Annealing** — ~50–65 °C, primers (~20 bp each, complementary to flanking regions) bind.',
        hindi: '• **एनीलिंग** - ~50-65 डिग्री सेल्सियस, प्राइमर (~20 बीपी प्रत्येक, फ़्लैंकिंग क्षेत्रों के पूरक) बांधते हैं।',
            },
            {
              english: '• **Extension** — 72 °C, **Taq DNA polymerase** synthesises new strand.',
        hindi: '• **एक्सटेंशन** - 72 डिग्री सेल्सियस, **टैक डीएनए पोलीमरेज़** नए स्ट्रैंड को संश्लेषित करता है।',
            },
            {
              english: 'Each cycle doubles target → **2ⁿ amplification** (~ **1 billion copies in 30 cycles**).',
        hindi: 'प्रत्येक चक्र लक्ष्य को दोगुना कर देता है → **2ⁿ प्रवर्धन** (~ **30 चक्रों में 1 बिलियन प्रतियां**)।',
            },
          ],
        },
        {
          type: 'equation',
          latex: '\\text{Copies after } n \\text{ cycles} = 2^n',
        },
      ],
    },
    {
      id: 'gel-blot',
      title: {
        english: 'Gel Electrophoresis & Blotting',
        hindi: 'जेल वैद्युत-कण संचलन और ब्लॉटिंग',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Agarose gel separates DNA fragments by size; smaller fragments migrate faster; visualised with EtBr + UV.',
        hindi: 'एगरोज़ जेल डीएनए के टुकड़ों को आकार के अनुसार अलग करता है; छोटे टुकड़े तेजी से पलायन करते हैं; EtBr + UV के साथ विज़ुअलाइज़ किया गया।',
            },
            {
              english: 'PAGE used for proteins.',
        hindi: 'पृष्ठ प्रोटीन के लिए उपयोग किया जाता है। **दक्षिणी धब्बा** — डीएनए। **उत्तरी धब्बा** - आरएनए। **वेस्टर्न ब्लॉट** - प्रोटीन (इम्यूनोब्लॉट)।',
            },
            {
              english: '**Southern blot** — DNA. **Northern blot** — RNA. **Western blot** — proteins (immunoblot).',
        hindi: '**दक्षिणी धब्बा** - डीएनए। **उत्तरी धब्बा** - आरएनए। **वेस्टर्न ब्लॉट** - प्रोटीन (इम्यूनोब्लॉट)।',
            },
            {
              english: 'Probe = labelled (radioactive ³²P or fluorescent) ssDNA/RNA complementary to target — used to identify specific sequence.',
        hindi: 'जांच = लेबल किया गया (रेडियोधर्मी ³²पी या फ्लोरोसेंट) एसएसडीएनए/आरएनए लक्ष्य का पूरक - विशिष्ट अनुक्रम की पहचान करने के लिए उपयोग किया जाता है।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{rDNA = vector + insert (cut by same restriction enzyme; joined by ligase)}',
      desc: {
        english: '',
        hindi: 'आरडीएनए = वेक्टर + इंसर्ट (समान प्रतिबंध एंजाइम द्वारा काटा गया; लिगेज द्वारा जोड़ा गया)',
      },
    },
    {
      latex: '\\text{EcoRI: GAATTC (palindrome) → sticky ends}',
      desc: {
        english: '',
        hindi: 'EcoRI: GAATTC (पैलिंड्रोम) → चिपचिपे सिरे',
      },
    },
    {
      latex: '\\text{Competent host: } Ca^{2+} + \\text{heat shock } 42^\\circ C',
      desc: {
        english: '',
        hindi: 'सक्षम मेज़बान: Ca 2+ + हीट शॉक 42 C',
      },
    },
    {
      latex: '\\text{Ti plasmid (Agrobacterium): plant vector}',
      desc: {
        english: '',
        hindi: 'टीआई प्लास्मिड (एग्रोबैक्टीरियम): पादप वेक्टर',
      },
    },
    {
      latex: '\\text{PCR: Denaturation 94°C} \\rightarrow \\text{Annealing 50-65°C} \\rightarrow \\text{Extension 72°C}',
      desc: {
        english: '',
        hindi: 'पीसीआर: विकृतीकरण 94°C एनीलिंग 50-65°C एक्सटेंशन 72°C',
      },
    },
    {
      latex: '\\text{Copies after } n \\text{ cycles} = 2^n \\,(\\approx 10^9 \\text{ in 30 cycles})',
      desc: {
        english: '',
        hindi: 'n चक्रों के बाद की प्रतिलिपियाँ = 2 n \\,(30 चक्रों में 10 9)',
      },
    },
    {
      latex: '\\text{Stirred-tank bioreactor: 100-1000 L; large-scale culture}',
      desc: {
        english: '',
        hindi: 'स्टिरर्ड-टैंक बायोरिएक्टर: 100-1000 एल; बड़े पैमाने की संस्कृति',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Enzyme',
      problem: {
        english: 'EcoRI recognises the sequence:',
        hindi: 'EcoRI अनुक्रम को पहचानता है:',
      },
      answer: {
        english: 'GAATTC (palindromic; 6 bp)',
        hindi: 'GAATTC (पेलिंड्रोमिक; 6 बीपी)',
      },
      solution: {
        english: 'Generates sticky ends.',
        hindi: 'चिपचिपे सिरे उत्पन्न करता है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Vector',
      problem: {
        english: 'Plant cloning vector commonly used is:',
        hindi: 'आमतौर पर इस्तेमाल किया जाने वाला प्लांट क्लोनिंग सदिश है:',
      },
      answer: {
        english: 'Ti plasmid of Agrobacterium tumefaciens',
        hindi: 'एग्रोबैक्टीरियम टूमफेशियन्स का Ti प्लास्मिड',
      },
      solution: {
        english: 'Natural genetic engineer; T-DNA integrates into plant genome.',
        hindi: 'प्राकृतिक आनुवंशिक इंजीनियर; टी-डीएनए पौधे के जीनोम में एकीकृत होता है।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'PCR',
      problem: {
        english: 'Thermostable DNA polymerase used in PCR is:',
        hindi: 'पीसीआर में प्रयुक्त थर्मोस्टेबल डीएनए पोलीमरेज़ है:',
      },
      answer: {
        english: 'Taq polymerase (from Thermus aquaticus)',
        hindi: 'टाक पोलीमरेज़ (थर्मस एक्वाटिकस से)',
      },
      solution: {
        english: 'Withstands 94 °C denaturation step.',
        hindi: '94 डिग्री सेल्सियस विकृतीकरण चरण का सामना करता है।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Selection',
      problem: {
        english: 'Insertional inactivation of antibiotic resistance gene helps in:',
        hindi: 'एंटीबायोटिक प्रतिरोध जीन को सम्मिलित रूप से निष्क्रिय करने से मदद मिलती है:',
      },
      answer: {
        english: 'Selection of recombinants',
        hindi: 'पुनः संयोजकों का चयन',
      },
      solution: {
        english: 'Recombinant cells lose resistance; non-recombinants retain.',
        hindi: 'पुनः संयोजक कोशिकाएं प्रतिरोध खो देती हैं; गैर-पुनः संयोजक बरकरार रहते हैं।',
      },
    },
    {
      year: 2018,
      exam: 'NEET',
      tag: 'Competent',
      problem: {
        english: 'Bacterial cells are made competent by treatment with:',
        hindi: 'जीवाणु कोशिकाओं को उपचार द्वारा सक्षम बनाया जाता है:',
      },
      answer: {
        english: 'Divalent calcium ions + heat shock',
        hindi: 'डाइवेलेंट कैल्शियम आयन + हीट शॉक',
      },
      solution: {
        english: '42 °C for 90 sec then 4 °C ice bath.',
        hindi: '90 सेकंड के लिए 42 डिग्री सेल्सियस फिर 4 डिग्री सेल्सियस बर्फ स्नान।',
      },
    },
  ],
  next: {
    slug: 'biotech-applications',
    title: 'Biotechnology and its Applications',
  },
};

export default chapter;
